<template>
  <ProgressLayout
    v-if="!isPendingEnvelopeProgress"
    :steps="steps"
    @goToStep="goToStep"
  >
    <KeepAlive :include="keepAliveComponentNames">
      <RouterView />
    </KeepAlive>
  </ProgressLayout>
</template>

<script setup>
// @ts-check
import { computed } from 'vue';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router/composables';

import {
  useClearEnvelopesCache,
  useEnvelopeProgress,
  useEnvelopesProgress,
  useProgressRoute,
} from '@/composables/envelopes';
import { useFlagEnabled } from '@/composables/featureFlags/useFlagEnabled';
import { watchFalse } from '@/composables/utils/watchFalse';

import ProgressLayout from '@/components/layouts/ProgressLayout';

const route = useRoute();
const router = useRouter();
const clearEnvelopesCache = useClearEnvelopesCache();

const fingerprint = computed(() => route.params.fingerprint);
const {
  envelopeSent,
  isPendingEnvelopeProgress,
  isFetchingEnvelopeProgress,
  isErrorEnvelopeProgress,
} = useEnvelopeProgress(fingerprint);
const progressRoute = useProgressRoute();
const { steps, stepsRoutes, nextStepsRoutes } = useEnvelopesProgress();

/**
 * Gets the names of the components on the routes that should be kept alive
 *
 * We only want to keep alive the routes that the user can make changes on (marked 'keepAlive' in router)
 * and can be navigated back from (next routes)
 */
const keepAliveComponentNames = computed(() =>
  nextStepsRoutes.value
    // map the routes to their component names or undefined if the route is not keepAlive
    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    .map(r => (r.meta.keepAlive ? r.component?.__name : undefined))
    // filter out the undefined values
    .filter(name => name));

const [flagLoading, flagEnabled] = useFlagEnabled('new-envelope-flow');

watchFalse(flagLoading, () => {
  if (!flagEnabled.value) {
    router.replace({ name: 'Envelopes' });
  }
});

// redirect (if required) when the envelope progress is loaded
watchFalse(isFetchingEnvelopeProgress, () => {
  // if the envelope is not found, redirect to the envelopes page
  if (isErrorEnvelopeProgress.value) {
    router.replace({ name: 'Envelopes' });
    return;
  }

  // if not on the correct step, redirect to the correct step
  if (route.name === 'EnvelopesSteps' || route.name !== progressRoute.value.name) {
    router.replace(progressRoute.value);
  }
});

onBeforeRouteUpdate((to, __, next) => {
  if (envelopeSent.value && to.name !== progressRoute.value.name) {
    next(progressRoute.value);
  }

  next();
});

onBeforeRouteLeave((to, __, next) => {
  if (!to.name || !/^Envelopes(Step|Edit).*$/.test(to.name)) {
    // clear query cache
    clearEnvelopesCache();
  }

  next();
});

/** @param {number} step */
function goToStep(step) {
  router.push({
    name: stepsRoutes[step].name,
    params: { fingerprint: route.params.fingerprint },
  });
}
</script>
